import type { ModuleDef } from "../types";
import { defineModule } from "./define";

/** `qualityMod` is the mined-ore quality multiplier: beams 1.0 (lossless),
 *  kinetic ~0.01 (ruins). Beams deposit `powerActiveW × energyEfficiency` W
 *  into the target and let thermal failure (m.tempK crossing meltPointK)
 *  destroy it; their `damage`, `muzzleVel`, `rof`, and `heatOnImpactJ`
 *  fields are unused. */
export const WEAPONS: ModuleDef[] = [
  defineModule({
    id: "pdc_mk1",
    name: "PDC Mk.I",
    category: "weapon",
    tier: 1,
    cells: { w: 1, h: 1 },
    sprite: "pdc_mk1",
    stats: {
      mass: 220, hp: 50,
      tensile: 600_000_000,
      hardness: 0.6,
      powerIdleW: 150,
      powerActiveW: 1_200,
      energyEfficiency: 0,
      thermalConductivity: 1.2,
      thermalEmissivity: 0.5,
      requiresCockpit: true,
    },
    weapon: {
      class: "ballistic",
      rof: 9, muzzleVel: 1_100, damage: 1, range: 320,
      projectileMass: 0.005, projectileSize: 1, projectileSprite: "round_small",
      densityKgPerM3: 8_960, specificHeatJPerKgK: 385,
      emissivity: 0.10, hardness: 0.30,
      blastRadiusM: 0, qualityMod: 0.01,
      heatOnImpactJ: 4_000, heatOnRicochetJ: 1_000,
      restitution: 0.1,
      spreadRad: 0.04,
      ammoMax: 1_200,
      ammoResource: "bullets",
      ammoRefillS: 0.1,
    },
  }),
  defineModule({
    id: "auto_cannon_mk1",
    name: "Autocannon Mk.I",
    category: "weapon",
    tier: 1,
    cells: { w: 2, h: 1 },
    sprite: "auto_cannon_mk1",
    stats: {
      mass: 380, hp: 80,
      tensile: 700_000_000,
      hardness: 0.7,
      powerIdleW: 250,
      powerActiveW: 1_500,
      energyEfficiency: 0,
      thermalConductivity: 1.2,
      thermalEmissivity: 0.5,
      requiresCockpit: true,
    },
    weapon: {
      class: "ballistic",
      rof: 6, muzzleVel: 1_080, damage: 4.5, range: 600,
      projectileMass: 0.05, projectileSize: 1, projectileSprite: "shell_med",
      densityKgPerM3: 7_850, specificHeatJPerKgK: 500,
      emissivity: 0.70, hardness: 0.60,
      blastRadiusM: 6, qualityMod: 0.005,
      heatOnImpactJ: 25_000, heatOnRicochetJ: 5_000,
      restitution: 0.05,
      spreadRad: 0.025,
      ammoMax: 240,
      ammoResource: "shells",
      ammoRefillS: 0.3,
    },
  }),
  defineModule({
    id: "rail_mk1",
    name: "Light Railgun Mk.I",
    category: "weapon",
    tier: 2,
    cells: { w: 3, h: 1 },
    sprite: "rail_mk1",
    stats: {
      mass: 820, hp: 90,
      tensile: 1_200_000_000,
      hardness: 0.8,
      crew: 1,
      powerIdleW: 1_500,
      powerActiveW: 22_000,
      energyEfficiency: 0.18,
      thermalConductivity: 2.0,
      thermalEmissivity: 0.4,
      requiresCockpit: true,
    },
    weapon: {
      class: "ballistic",
      rof: 0.5, muzzleVel: 2_500, damage: 80, range: 4_000,
      projectileMass: 2.0, projectileSize: 1, projectileSprite: "slug_rail",
      densityKgPerM3: 19_300, specificHeatJPerKgK: 134,
      emissivity: 0.40, hardness: 0.90,
      blastRadiusM: 4, qualityMod: 0.001,
      heatOnImpactJ: 250_000, heatOnRicochetJ: 0,
      restitution: 0,
      spreadRad: 0,
      ammoMax: 40,
      ammoResource: "slugs",
      ammoRefillS: 1.5,
    },
  }),
  defineModule({
    id: "mining_laser_mk1",
    name: "Mining Laser Mk.I",
    category: "weapon",
    tier: 1,
    cells: { w: 1, h: 1 },
    sprite: "mining_laser_mk1",
    stats: {
      mass: 260, hp: 55,
      tensile: 400_000_000,
      hardness: 0.3,
      powerIdleW: 50_000,
      powerActiveW: 62_500_000,
      energyEfficiency: 0.4,
      thermalConductivity: 1.1,
      thermalEmissivity: 0.3,
      crewTraversable: false,
      requiresCockpit: true,
    },
    weapon: {
      class: "energy",
      kind: "beam",
      altFire: true,
      rof: 0, muzzleVel: 0,
      damage: 0,
      range: 180,
      projectileMass: 0, projectileSize: 0, projectileSprite: "round_small",
      beamSprite: "mining_beam", beamWidthM: 2.4, beamRayCount: 16,
      blastRadiusM: 0, qualityMod: 1.0,
      heatOnImpactJ: 0, heatOnRicochetJ: 0,
      restitution: 0,
      spreadRad: 0,
    },
  }),
  defineModule({
    id: "missile_rack_mk1",
    name: "Missile Rack Mk.I",
    category: "weapon",
    tier: 2,
    cells: { w: 2, h: 1 },
    sprite: "missile_rack_mk1",
    stats: {
      mass: 560, hp: 60,
      tensile: 400_000_000,
      hardness: 0.4,
      powerIdleW: 200,
      powerActiveW: 800,
      energyEfficiency: 0,
      thermalConductivity: 0.9,
      thermalEmissivity: 0.5,
      requiresCockpit: true,
    },
    weapon: {
      class: "missile",
      rof: 0.3, muzzleVel: 500, damage: 150, range: 8_000,
      projectileMass: 80, projectileSize: 3, projectileSprite: "missile_basic",
      densityKgPerM3: 7_850, specificHeatJPerKgK: 500,
      emissivity: 0.70, hardness: 0.60,
      blastRadiusM: 18, qualityMod: 0,
      heatOnImpactJ: 600_000, heatOnRicochetJ: 0,
      restitution: 0,
      spreadRad: 0.015,
      ammoMax: 12,
      ammoResource: "missiles",
      ammoRefillS: 3,
    },
  }),
];
